import { canUseDOM } from '@/utils/dom-utils';
import { getGlobalConfigs } from '@/utils/site-utils';
import { hasConsent, waitForCookiebot, onCookiebotConsentChange } from '@/utils/cookiebot-utils';

let hubspotInjected = false;
let hubspotAutoLoadEnabled = false;

const initHubSpotQueue = () => {
  if (!canUseDOM()) return;
  window._hsq = window._hsq || [];
};

export const loadHubSpot = async (page) => {
  if (hubspotInjected) {
    console.log('HubSpot already injected, skipping...');
    return;
  }

  const cookiebot = await waitForCookiebot();
  if (cookiebot && !hasConsent('marketing')) {
    console.log('User has not consented to marketing cookies, HubSpot not loaded');
    return null;
  }

  const [hubspotId] = getGlobalConfigs(page, 'hubspotId');
  console.log('Loading HubSpot with ID:', hubspotId?.value);

  if (!hubspotId?.value || typeof window === 'undefined') {
    console.log('HubSpot ID not available or not in browser environment');
    return;
  }

  try {
    initHubSpotQueue();

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.id = 'hs-script-loader';
    script.async = true;
    script.defer = true;
    script.src = `//js.hs-scripts.com/${hubspotId.value}.js`;
    document.head.appendChild(script);

    hubspotInjected = true;
    console.log('HubSpot injected successfully');
  } catch (error) {
    console.error('Failed to load HubSpot:', error);
  }
};

export const enableHubSpotAutoLoad = (page) => {
  if (hubspotAutoLoadEnabled) {
    return () => {};
  }

  hubspotAutoLoadEnabled = true;

  const checkExistingConsent = async () => {
    const cookiebot = await waitForCookiebot();
    if (cookiebot && hasConsent('marketing')) {
      try {
        await loadHubSpot(page);
        return true;
      } catch (error) {
        console.error('Failed to load HubSpot based on existing consent:', error);
      }
    }
    return false;
  };

  checkExistingConsent();

  const cleanup = onCookiebotConsentChange(async ({ marketing }) => {
    if (marketing) {
      try {
        await loadHubSpot(page);
      } catch (error) {
        console.error('Failed to load HubSpot after marketing consent:', error);
      }
    }
  });

  return () => {
    cleanup();
    hubspotAutoLoadEnabled = false;
  };
};

export const hubspotPush = (action, ...params) => {
  if (!canUseDOM()) return;
  if (!hasConsent('marketing')) {
    console.log('User has not consented to marketing cookies, HubSpot event not pushed');
    return;
  }
  initHubSpotQueue();
  window._hsq.push([action, ...params]);
};

export const isHubSpotLoaded = () => {
  return hubspotInjected;
};

export const resetHubSpotState = () => {
  hubspotInjected = false;
  hubspotAutoLoadEnabled = false;
};

export const stopHubSpot = () => {
  if (!canUseDOM()) return;
  const script = document.getElementById('hs-script-loader');
  if (script) {
    script.remove();
  }
  hubspotInjected = false;
};
